/* ============================================================
   Fence Works — goal planner math (Budget & Cash → Budget)
   Plain functions, no DOM and no store: business.tsx hands them the
   store's quotes / work orders / crews / customers and renders what
   comes back, and scripts/unit.mjs runs them in a bare vm.

   Months are carried as one integer, year * 12 + month (0-based), so
   "Sep 2026" is 2026 * 12 + 8 and month arithmetic is plain + / −.
   ============================================================ */

const PL_DAY = 86400000;
const PL_WON = ["signed", "paid"];

/* a typical fence year, % of annual revenue by month: slow Dec–Feb (frozen
   ground, holidays), building through spring, peak May–Sep. Sums to 100. */
const TYPICAL_SEASON = [4, 4.5, 6.5, 8.5, 10.5, 11, 11, 11, 10.5, 9, 7.5, 6];

function monthIdxOfMs(t: any): number | null {
  const n = Number(t);
  if (!(n > 0)) return null;
  const d = new Date(n);
  return d.getFullYear() * 12 + d.getMonth();
}
function monthIdxOfYmd(s: any): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? +m[1] * 12 + (+m[2] - 1) : null;
}

/* 12 weights → 12 fractions that sum to 1. Anything unusable (wrong length,
   negatives, all zero) falls back to the typical curve, so the plan never
   divides by zero or goes negative. */
function normalizeSeason(w: any): number[] {
  const ok = Array.isArray(w) && w.length === 12 && w.every((x: any) => Number.isFinite(Number(x)) && Number(x) >= 0);
  const src = ok ? w.map(Number) : TYPICAL_SEASON;
  const sum = src.reduce((a: number, b: number) => a + b, 0);
  if (!(sum > 0)) return TYPICAL_SEASON.map((x) => x / 100);
  return src.map((x: number) => x / sum);
}

/* set one month's share (in %) and rescale the other eleven so the year still
   adds to 100 — a +/− on May moves weight out of the other months, it doesn't
   quietly grow the annual goal. */
function setSeasonMonth(w: any, m: number, pct: number): number[] {
  const cur = normalizeSeason(w).map((x) => x * 100);
  const v = Math.max(0, Math.min(40, Number(pct) || 0));
  const rest = cur.reduce((a, b, i) => (i === m ? a : a + b), 0);
  const out = cur.map((x, i) => {
    if (i === m) return v;
    return rest > 0 ? (x / rest) * (100 - v) : (100 - v) / 11;
  });
  return out.map((x) => Math.round(x * 100) / 100);
}

/* Revenue that actually happened / is booked, by month.
     done  — completed work orders, in the month they finished (completedAt,
             else the install start), plus paid quotes that never got a work
             order (in the month they were won)
     sched — work orders not finished yet, in the month they're scheduled to start
   Signed-but-unscheduled work is neither — it's the backlog. */
function revenueByMonth(workOrders: any[], quotes: any[]) {
  const done: Record<number, number> = {}, sched: Record<number, number> = {};
  const add = (map: Record<number, number>, k: number | null, amt: number) => { if (k != null && amt > 0) map[k] = (map[k] || 0) + amt; };
  const wos = workOrders || [];
  const hasWo = new Set(wos.map((w: any) => w && w.quoteId).filter(Boolean));
  wos.forEach((wo: any) => {
    if (!wo) return;
    const amt = Number(wo.total) || 0;
    if (/^(done|completed)$/.test(String(wo.status || ""))) add(done, monthIdxOfMs(wo.completedAt) ?? monthIdxOfYmd(wo.scheduledStart), amt);
    else add(sched, monthIdxOfYmd(wo.scheduledStart), amt);
  });
  (quotes || []).forEach((q: any) => {
    if (!q || q.status !== "paid" || hasWo.has(q.id)) return;
    add(done, monthIdxOfMs(q.wonAt || q.lastSentAt || q.createdAt), Number(q.total) || 0);
  });
  return { done, sched };
}

/* Seasonality from the shop's own last 24 full months (the current, partial
   month is left out). Needs at least 8 months with revenue; below that it
   returns weights: null and the planner uses the typical curve. A calendar
   month the history doesn't cover yet (a business under a year old) is
   filled from the typical curve, scaled to the months it does cover. */
function seasonFromHistory(done: Record<number, number>, nowMs: number) {
  const cur = monthIdxOfMs(nowMs) as number;
  const lo = cur - 24, hi = cur - 1;
  let first: number | null = null, monthsWithData = 0;
  for (let k = lo; k <= hi; k++) {
    if ((done[k] || 0) > 0) { monthsWithData++; if (first == null) first = k; }
  }
  if (monthsWithData < 8 || first == null) return { weights: null, monthsWithData };
  const sum = Array(12).fill(0), n = Array(12).fill(0);
  for (let k = first; k <= hi; k++) { sum[k % 12] += done[k] || 0; n[k % 12]++; }
  const avg = sum.map((s, i) => (n[i] ? s / n[i] : null));
  let seen = 0, typ = 0;
  avg.forEach((a, i) => { if (a != null) { seen += a; typ += TYPICAL_SEASON[i]; } });
  const scale = typ > 0 ? seen / typ : 0;
  const filled = avg.map((a, i) => (a != null ? a : TYPICAL_SEASON[i] * scale));
  const total = filled.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return { weights: null, monthsWithData };
  return { weights: filled.map((x) => Math.round((x / total) * 10000) / 100), monthsWithData };
}

/* The four buckets (+ commissions). magic = variable cost per $1 of revenue.
   Goal = the monthly revenue that pays overhead AND leaves the target net;
   break-even pays overhead and leaves $0. Null when the buckets eat the dollar. */
function planBuckets(b: any) {
  const overheadMo = Math.max(0, Number(b.overheadMo) || 0);
  const magic = ((Number(b.laborPct) || 0) + (Number(b.commPct) || 0) + (Number(b.matPct) || 0)) / 100;
  const netFrac = (Number(b.netPct) || 0) / 100;
  const denom = 1 - magic - netFrac;
  const revGoalMo = denom > 0.01 ? overheadMo / denom : null;
  const breakevenMo = magic < 0.99 ? overheadMo / (1 - magic) : null;
  return { overheadMo, magic, netFrac, denom, revGoalMo, breakevenMo, annualGoal: revGoalMo != null ? revGoalMo * 12 : null };
}

/* annual goal spread over the year by season, times a scenario multiplier */
function monthlyPlan(annual: number, weights: any, mult?: number): number[] {
  const w = normalizeSeason(weights);
  const k = mult == null ? 1 : Math.max(0, Number(mult) || 0);
  return w.map((x) => (Number(annual) || 0) * x * k);
}

/* days in a calendar month that fall on the given weekdays (0 = Sun … 6 = Sat) */
function workingDays(year: number, month: number, workdays: any): number {
  const days = Array.isArray(workdays) && workdays.length ? workdays.map(Number) : [1, 2, 3, 4, 5];
  const last = new Date(year, month + 1, 0).getDate();
  let n = 0;
  for (let d = 1; d <= last; d++) if (days.includes(new Date(year, month, d).getDay())) n++;
  return n;
}

/* crew-hours a month can hold: for every crew, headcount × hours/day × its
   workdays in that month, less the share lost to weather and holidays. The
   same unit as the schedule's capacity strip and a work order's crew hours.
   No crews → 0 (the planner says "add a crew" rather than inventing one). */
function crewMonthHours(crews: any[], year: number, month: number, fallbackWorkdays: any, lostPct: number): number {
  const lost = Math.max(0, Math.min(90, Number(lostPct) || 0)) / 100;
  let h = 0;
  (crews || []).forEach((c: any) => {
    if (!c) return;
    const heads = Array.isArray(c.members) && c.members.length ? c.members.length : (Number(c.size) || 0);
    const hpd = Number(c.hoursPerDay) || 8;
    const wd = Array.isArray(c.workdays) && c.workdays.length ? c.workdays : fallbackWorkdays;
    h += heads * hpd * workingDays(year, month, wd);
  });
  return h * (1 - lost);
}

/* revenue per quoted crew-hour from [{ total, hours }] — null with no usable pairs */
function revPerCrewHour(pairs: any[]): number | null {
  let t = 0, h = 0;
  (pairs || []).forEach((p: any) => {
    const tt = Number(p && p.total) || 0, hh = Number(p && p.hours) || 0;
    if (tt > 0 && hh > 0) { t += tt; h += hh; }
  });
  return h > 0 ? t / h : null;
}

/* Commission as a % of ALL revenue — what it costs per $1 sold, the unit the
   other buckets use. Each won quote (last 12 months) pays its rep's roster %;
   jobs with no rostered rep pay 0. With a roster but no won work yet, the
   plain average of the roster's rates stands in. No roster → 0. */
function commissionRate(quotes: any[], roster: any[], nowMs: number) {
  const key = (s: any) => String(s || "").trim().toLowerCase();
  const reps = (roster || []).filter((r: any) => r && (r.name || r.email));
  const pctOf = (name: any) => {
    const k = key(name);
    if (!k) return 0;
    const r = reps.find((x: any) => key(x.name) === k || (x.email && key(x.email) === k));
    return r ? Math.max(0, Number(r.commissionPct) || 0) : 0;
  };
  if (!reps.length) return { pct: 0, source: "none", revenue: 0 };
  const from = nowMs - 365 * PL_DAY;
  let rev = 0, comm = 0;
  (quotes || []).forEach((q: any) => {
    if (!q || !PL_WON.includes(q.status)) return;
    const at = Number(q.wonAt) || Number(q.lastSentAt) || Number(q.createdAt) || 0;
    if (at < from) return;
    const t = Number(q.total) || 0;
    rev += t; comm += t * pctOf(q.rep) / 100;
  });
  if (rev > 0) return { pct: Math.round((comm / rev) * 1000) / 10, source: "won", revenue: rev };
  const rates = reps.map((r: any) => Math.max(0, Number(r.commissionPct) || 0));
  return { pct: Math.round((rates.reduce((a: number, b: number) => a + b, 0) / rates.length) * 10) / 10, source: "roster", revenue: 0 };
}

/* The sales funnel from the shop's own records, the same way Reports counts it:
     win rate  = won ÷ all quotes created in the window
     avg job   = won revenue ÷ won jobs
     lead→quote = new customers in the window who got a quote ÷ new customers
     cost/lead = monthly marketing spend (Reports → Lead sources) ÷ new customers
   Each number uses the last 90 days when there's enough to go on, else the
   last 12 months, else null (the UI shows an editable default instead). */
function funnelStats(input: any) {
  const quotes = (input && input.quotes) || [], customers = (input && input.customers) || [];
  const spendMo = Math.max(0, Number(input && input.spendMo) || 0);
  const nowMs = Number(input && input.nowMs) || Date.now();
  const norm = (s: any) => String(s || "").trim().toLowerCase();
  const win = (days: number) => {
    const from = nowMs - days * PL_DAY;
    const qs = quotes.filter((q: any) => q && (Number(q.createdAt) || 0) >= from);
    const won = qs.filter((q: any) => PL_WON.includes(q.status));
    const wonTotal = won.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
    const cs = customers.filter((c: any) => c && (Number(c.createdAt) || 0) >= from);
    const quoted = cs.filter((c: any) => quotes.some((q: any) => q && ((c.id && q.customerId === c.id) || (c.name && norm(q.customer) === norm(c.name)))));
    return { days, quotes: qs.length, won: won.length, wonTotal, leads: cs.length, quoted: quoted.length };
  };
  const w90 = win(90), w365 = win(365);
  const pick = (ok: (w: any) => boolean) => (ok(w90) ? w90 : ok(w365) ? w365 : null);
  const wr = pick((w) => w.quotes >= 3);
  const aj = pick((w) => w.won >= 1 && w.wonTotal > 0);
  const lq = pick((w) => w.leads >= 3);
  const cl = spendMo > 0 ? pick((w) => w.leads >= 1) : null;
  return {
    winRate: wr ? { value: wr.won / wr.quotes, days: wr.days, n: wr.quotes } : null,
    avgJob: aj ? { value: aj.wonTotal / aj.won, days: aj.days, n: aj.won } : null,
    leadToQuote: lq ? { value: Math.min(1, lq.quoted / lq.leads), days: lq.days, n: lq.leads } : null,
    costPerLead: cl ? { value: (spendMo * (cl.days / 30.4)) / cl.leads, days: cl.days, n: cl.leads } : null,
  };
}

/* Work a revenue goal back to the funnel: jobs → quotes → leads → marketing $.
   Any missing link stops the chain there (null), it never guesses. Counts
   round up — you can't win 0.4 of a job. Rates are fractions (0.35 = 35%). */
function workBackwards(goal: number, f: any) {
  const g = Math.max(0, Number(goal) || 0);
  const avgJob = Number(f && f.avgJob) || 0, winRate = Number(f && f.winRate) || 0, l2q = Number(f && f.leadToQuote) || 0;
  const cpl = f && f.costPerLead != null && Number.isFinite(Number(f.costPerLead)) ? Math.max(0, Number(f.costPerLead)) : null;
  const jobs = avgJob > 0 ? Math.ceil(g / avgJob - 1e-9) : null;
  const quotes = jobs != null && winRate > 0 ? Math.ceil(jobs / Math.min(1, winRate) - 1e-9) : null;
  const leads = quotes != null && l2q > 0 ? Math.ceil(quotes / Math.min(1, l2q) - 1e-9) : null;
  const marketing = leads != null && cpl != null ? leads * cpl : null;
  return { jobs, quotes, leads, marketing };
}

/* planned revenue by quarter → variable costs (magic × revenue), three months
   of overhead, and what's left: planned net. Index 4 is the year. */
function quarterlyPlan(monthly: number[], magic: number, overheadMo: number) {
  const rows = [0, 1, 2, 3].map((q) => {
    const revenue = [0, 1, 2].reduce((a, i) => a + (Number(monthly[q * 3 + i]) || 0), 0);
    const variable = revenue * (Number(magic) || 0);
    const overhead = 3 * (Number(overheadMo) || 0);
    return { label: "Q" + (q + 1), revenue, variable, overhead, net: revenue - variable - overhead };
  });
  const sum = (k: string) => rows.reduce((a: number, r: any) => a + r[k], 0);
  rows.push({ label: "Year", revenue: sum("revenue"), variable: sum("variable"), overhead: sum("overhead"), net: sum("net") });
  return rows;
}

/* what a split of net profit looks like. Buckets are { id, label, pct }; the
   split only counts when it adds to 100 (ok: false otherwise) — a plan that
   hands out 110% of the profit isn't a plan. */
const DEFAULT_SPLIT = [
  { id: "owner", label: "Owner pay", pct: 35 },
  { id: "taxes", label: "Taxes", pct: 30 },
  { id: "reserve", label: "Cash reserve", pct: 20 },
  { id: "reinvest", label: "Reinvest", pct: 15 },
];
function allocateProfit(net: number, split: any) {
  const rows = (Array.isArray(split) && split.length ? split : DEFAULT_SPLIT).map((s: any) => ({ id: s.id, label: s.label, pct: Math.max(0, Number(s.pct) || 0) }));
  const total = Math.round(rows.reduce((a: number, r: any) => a + r.pct, 0) * 100) / 100;
  const ok = Math.abs(total - 100) < 0.01;
  const n = Math.max(0, Number(net) || 0);
  return { ok, total, rows: rows.map((r: any) => ({ ...r, amount: ok ? (n * r.pct) / 100 : null })) };
}

export {
  TYPICAL_SEASON, DEFAULT_SPLIT, monthIdxOfMs, monthIdxOfYmd, normalizeSeason, setSeasonMonth, revenueByMonth, seasonFromHistory,
  planBuckets, monthlyPlan, workingDays, crewMonthHours, revPerCrewHour, commissionRate, funnelStats, workBackwards,
  quarterlyPlan, allocateProfit,
};
